import React, { useEffect, useRef, useState } from 'react';
import DashboardPlaceholder from './DashboardPlaceholder.jsx';
import DashboardTable from './DashboardTable.jsx';
import DetailedTable from './DetailedTable.jsx';
import { resolveStaticUrl } from '../../utils/staticAssets.js';

const LOGO_SRC = resolveStaticUrl('images/logo.png');
const PLACEHOLDER_SRC = resolveStaticUrl('images/placeholder.png');

const DashboardLayout = ({
  authSection,
  historyButton,
  weeksCheckboxes,
  errorBanner,
  warmerName,
  showWarmerBanner = true,
  kpiContent,
  restaurantSelect,
  processSelector,
  generateButton,
  clockHeader,
  hoursControl,
  viewState = 'placeholder',
  placeholderState,
  dashboardTable,
  dashboardStocks,
  onDashboardStockChange,
  dashboardTableFilter = 'all',
  onDashboardFilterChange,
  detailedTable,
  summaryTitle,
  summarySubtitle,
  summaryContent,
  gamificationContent,
  additionalInfoContent,
  forecastModal,
  processesJson,
  saveControls,
  saveStatus = 'idle',
  onSaveForecast,
  onExportRequest,
  onExportForecastCsv,
  exporting = false,
  exportingFormat = null,
  exportingCsv = false,
  exportReady = false,
  canExportForecastCsv = false,
  azucarGlassMode = false
}) => {
  const sidebarRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const weeksContent = weeksCheckboxes === null
    ? null
    : (weeksCheckboxes || (<p className="loading-text">Seleccione al menos una semana.</p>));
  const saveVisible = !!saveControls?.visible;
  const canSaveForecast = !!saveControls?.hasUnsavedChanges;
  const forecastAvailable = !!saveControls?.forecastAvailable;
  let saveButtonIcon = 'save';
  let saveButtonLabel = 'Guardar Pronóstico';
  let disableSaveButton = false;

  if (saveStatus === 'saving') {
    saveButtonIcon = 'loader';
    saveButtonLabel = 'Guardando...';
    disableSaveButton = true;
  } else if (saveStatus === 'success') {
    saveButtonIcon = 'check-circle';
    saveButtonLabel = 'Pronóstico guardado';
    disableSaveButton = true;
  } else if (saveStatus === 'error') {
    saveButtonIcon = 'alert-triangle';
    saveButtonLabel = 'Error al guardar';
    disableSaveButton = !canSaveForecast;
  } else if (!canSaveForecast) {
    saveButtonIcon = 'check-circle';
    saveButtonLabel = 'Ya Guardado';
    disableSaveButton = true;
  }

  const isExporting = Boolean(exportingFormat) || exporting;
  const exportDisabled = isExporting || !exportReady || saveStatus === 'saving';
  const isPdfProcessing = isExporting && exportingFormat === 'pdf';
  const isJpgProcessing = isExporting && exportingFormat === 'jpg';
  const exportCsvDisabled = exportingCsv || exportDisabled || !canExportForecastCsv;
  const getExportEnabledClass = (isProcessing) => (
    exportReady && (isProcessing || !exportDisabled) ? 'export-enabled' : ''
  );

  const handleSaveClick = () => {
    if (typeof onSaveForecast === 'function' && !disableSaveButton) {
      onSaveForecast();
    }
  };

  const handleExportClick = (format) => {
    if (typeof onExportRequest === 'function' && !exportDisabled) {
      onExportRequest(format);
    }
  };

  const handleExportCsvClick = () => {
    if (typeof onExportForecastCsv === 'function' && !exportCsvDisabled) {
      onExportForecastCsv();
    }
  };

  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (window.innerWidth > 767 || !isSidebarOpen) {
        return;
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeSidebar();
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        closeSidebar();
      }
    };
    const handleResize = () => {
      if (window.innerWidth > 767 && isSidebarOpen) {
        closeSidebar();
      }
    };

    window.addEventListener('keyup', handleEscape);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keyup', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', isSidebarOpen);
    return () => document.body.classList.remove('sidebar-open');
  }, [isSidebarOpen]);

  return (
    <>

    <header className="main-header">
        <div className="header-left">
            <a href="/"><img src={LOGO_SRC} alt="Tim Hortons" className="header-logo" /></a>
            <h1 className="system-title desktop-only">Hat Trick</h1>
        </div>
        <div className="header-center">
            {clockHeader}
        </div>
        <div className="header-right">
            {authSection}
        </div>
    </header>
    <div
      className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`}
      onClick={closeSidebar}
      role="presentation"
    />
    {errorBanner}
    <div className={`main-layout${azucarGlassMode ? ' azucar-glass-mode' : ''}`}>
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`} id="sidebar" ref={sidebarRef}>
            <button
              className="sidebar-toggle"
              id="sidebarToggle"
              type="button"
              aria-expanded={isSidebarOpen}
              aria-controls="sidebar"
              data-react-controlled="true"
              onClick={toggleSidebar}
            >
                <span className="hamburger"></span>
            </button>
            <div className="sidebar-content">
                <div className="config-section">
                    <div className="form-group">
                        <label htmlFor="restaurante-select" className="form-label">Restaurante</label>
                        {restaurantSelect}
                    </div>
                    <div className="form-group">
                        <label className="form-label">Procesos a Incluir</label>
                        <div id="procesos-container" className="checkbox-grid">
                            {processSelector}
                        </div>
                    </div>
                    {weeksContent ? (
                      <div className="form-group weeks-panel">
                          {weeksContent}
                      </div>
                    ) : null}
                </div>
                <div className="sidebar-footer">
                    {generateButton}
                    {historyButton}
                </div>
            </div>
        </aside>
        <main className="main-content">
            {warmerName && showWarmerBanner ? (
              <div className="warmer-banner" aria-label={`Warmer asignado: ${warmerName}`}>
                <div className="warmer-banner-glow" />
                <div className="warmer-banner-text">
                  <span className="warmer-badge-label">Warmer activo</span>
                  <span className="warmer-badge-name">{warmerName}</span>
                </div>
              </div>
            ) : null}
            <div id="kpi-section" className="kpi-section" style={{ display: viewState === 'data' ? undefined : 'none' }}>
                {kpiContent || (
                  <>
                    <div className="kpi-card">
                      <div className="kpi-value">0</div>
                      <div className="kpi-label">Artículos Totales</div>
                    </div>
                    <div className="kpi-card critical">
                      <div className="kpi-value">0</div>
                      <div className="kpi-label">A Producir</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-value">0 min</div>
                      <div className="kpi-label">Tiempo Total Prep.</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-value">0%</div>
                      <div className="kpi-label">Cobertura Stock</div>
                    </div>
                  </>
                )}
            </div>
            <div id="production-summary" className="production-summary" style={{ display: viewState === 'data' ? undefined : 'none' }}>
                <div className="summary-header">
                    <div>
                        <h3 className="summary-title">{summaryTitle || 'Unidades a producir de acuerdo a tiempo de vida'}</h3>
                        <p className="summary-subtitle">{summarySubtitle || 'Resumen visual de producción requerida'}</p>
                    </div>
                <div className="summary-actions">
                    {hoursControl}
                </div>
                </div>
                <div id="summary-grid" className="summary-grid">
                    {summaryContent}
                </div>
                {additionalInfoContent ? (
                  <div className="additional-info-section">
                    {additionalInfoContent}
                  </div>
                ) : null}
                <div className="forecast-save-container" style={{ display: saveVisible ? undefined : 'none' }} id="forecast-save-container">
                    <button
                      id="saveForecastBtn"
                      className="new-action-btn primary save-forecast-btn"
                      disabled={disableSaveButton}
                      onClick={handleSaveClick}
                    >
                        <span className="btn-icon"><i data-lucide={saveButtonIcon}></i></span>
                        <span className="btn-text">{saveButtonLabel}</span>
                    </button>
                    <div className="export-actions">
                    <button
                      id="exportPdfBtn"
                      className={`new-action-btn secondary ${getExportEnabledClass(isPdfProcessing)} ${isPdfProcessing ? 'processing' : ''}`}
                      title={isPdfProcessing ? 'Generando PDF...' : 'Exportar PDF'}
                      aria-busy={isPdfProcessing}
                      disabled={exportDisabled}
                      onClick={() => handleExportClick('pdf')}
                    >
                        <span className="btn-icon">
                          {isPdfProcessing ? <span className="processing-spinner" aria-hidden="true" /> : <i data-lucide="file-text" width="18" height="18"></i>}
                        </span>
                        <span className="btn-text">{isPdfProcessing ? 'Generando PDF' : 'Exportar PDF'}</span>
                    </button>
                    <button
                      id="exportPngBtn"
                      className={`new-action-btn secondary ${getExportEnabledClass(isJpgProcessing)} ${isJpgProcessing ? 'processing' : ''}`}
                      title={isJpgProcessing ? 'Generando JPG...' : 'Exportar JPG'}
                      aria-busy={isJpgProcessing}
                      disabled={exportDisabled}
                      onClick={() => handleExportClick('jpg')}
                    >
                        <span className="btn-icon">
                          {isJpgProcessing ? <span className="processing-spinner" aria-hidden="true" /> : <i data-lucide="image" width="18" height="18"></i>}
                        </span>
                        <span className="btn-text">{isJpgProcessing ? 'Generando JPG' : 'Exportar JPG'}</span>
                    </button>
                    {canExportForecastCsv ? (
                      <button
                        id="exportCsvBtn"
                        className={`new-action-btn excel ${exportReady ? 'export-enabled' : ''} ${exportingCsv ? 'processing' : ''}`}
                        title={exportingCsv ? 'Generando CSV...' : 'Exportar forecast CSV'}
                        aria-busy={exportingCsv}
                        disabled={exportCsvDisabled}
                        onClick={handleExportCsvClick}
                      >
                        <span className="btn-icon">
                          {exportingCsv ? <span className="processing-spinner" aria-hidden="true" /> : <i data-lucide="table" width="18" height="18"></i>}
                        </span>
                        <span className="btn-text">{exportingCsv ? 'Generando CSV' : 'Exportar forecast CSV'}</span>
                      </button>
                    ) : null}
                    </div>
                </div>
            </div>
            <div id="view-controls" className="view-controls" style={{ display: viewState === 'data' ? undefined : 'none' }}>
                <div className="view-toggle">
                    <button className="view-btn active" data-view="dashboard"><i data-lucide="bar-chart-3"></i> Dashboard</button>
                    <button className="view-btn" data-view="detailed"><i data-lucide="list"></i> Detallado</button>
                </div>
                <div className="view-actions">
                    <button id="refreshBtn" className="action-btn secondary"><i data-lucide="refresh-cw"></i> Actualizar</button>
                </div>
            </div>
            <div id="dashboard-view" className="data-view" style={{ display: viewState === 'data' ? undefined : 'none' }}>
                <div className="dashboard-header">
                    <h2>Dashboard de Producción</h2>
                </div>
                <DashboardTable
                  data={dashboardTable}
                  filter={dashboardTableFilter}
                  onFilterChange={onDashboardFilterChange}
                  stocks={dashboardStocks}
                  onStockChange={onDashboardStockChange}
                />
            </div>
            <div id="detailed-view" className="data-view" style={{ display: viewState === 'data' ? undefined : 'none' }}>
                <DetailedTable data={detailedTable} />
            </div>
            <div id="restaurant-selection-container" className="restaurant-selection-container" style={{ display: 'none' }}>
                <img src={LOGO_SRC} alt="Selecciona tu restaurante" className="restaurant-selection-image" />
            </div>
            <div id="placeholder" className="placeholder-content placeholder-content--strip" style={{ display: viewState === 'placeholder' ? 'flex' : 'none' }}>
                <DashboardPlaceholder placeholder={placeholderState} />
            </div>
            {gamificationContent ? (
              <div className="gamification-slot" style={{ display: viewState === 'loading' ? 'none' : undefined }}>
                {gamificationContent}
              </div>
            ) : null}
            <div id="loading-state" className="loading-state" style={{ display: viewState === 'loading' ? 'flex' : 'none' }}>
                <div className="loading-spinner"></div>
                <h3>Consultando BigQuery...</h3>
                <p>Analizando datos históricos y generando pronósticos</p>
                <div className="loading-progress">
                    <div className="progress-bar"></div>
                </div>
            </div>
        </main>
    </div>
    <div id="production-modal" className="modal">
        <div className="modal-content ux-modal">
            <div className="ux-modal-header">
                <div className="ux-header-content">
                    <div className="ux-header-title-row">
                        <img id="modal-item-image" className="ux-item-image" src={PLACEHOLDER_SRC} alt="Item" />
                        <div className="ux-title-content">
                            <h2 id="modal-title">Editar Pronóstico</h2>
                            <p className="ux-subtitle" id="modal-subtitle">Ajustar stock y producción</p>
                        </div>
                    </div>
                    <div className="ux-header-meta">
                        <div className="ux-chip" id="modal-header-lifespan">--</div>
                        <div className="ux-chip" id="modal-header-range">--</div>
                    </div>
                    <input type="hidden" id="modal-forecast-lifespan" />
                </div>
                <button className="ux-close-btn" id="production-modal-close">
                    <i data-lucide="x"></i>
                </button>
            </div>
            <div className="ux-modal-body">
                <input type="hidden" id="modal-article-id" />
                <div className="ux-motivos-container" style={{ marginBottom: '0.25rem' }}>
                    <div className="ux-motivo-row">
                        <div className="ux-motivo-info">
                            <div className="ux-motivo-icon">
                                <i data-lucide="target"></i>
                            </div>
                            <div className="ux-motivo-details">
                                <h4 className="ux-motivo-name">Pronósticos</h4>
                                <p className="ux-motivo-description">Original y Ajustado</p>
                            </div>
                        </div>
                        <div className="ux-input-container">
                            <span className="ux-inline-label">Original</span>
                            <div className="ux-total-value ux-neutral" id="modal-forecast-display">--</div>
                            <span className="ux-inline-label">Ajustado</span>
                            <div className="ux-total-value ux-positive" id="modal-adjusted-forecast-display">--</div>
                            <input type="hidden" id="modal-forecast" />
                            <input type="hidden" id="modal-adjusted-forecast" />
                        </div>
                    </div>
                    <div className="ux-motivo-row">
                        <div className="ux-motivo-info">
                            <div className="ux-motivo-icon">
                                <i data-lucide="package"></i>
                            </div>
                            <div className="ux-motivo-details">
                                <h4 className="ux-motivo-name">Stock</h4>
                                <p className="ux-motivo-description">Actual y duración</p>
                            </div>
                        </div>
                        <div className="ux-input-container">
                            <input type="number" id="modal-stock" className="ux-adjustment-input" placeholder="0" min="0" />
                            <span className="ux-percentage-symbol">unidades</span>
                            <div className="ux-total-value ux-neutral" id="modal-stock-duration">--</div>
                        </div>
                    </div>
                    <div className="ux-motivo-row">
                        <div className="ux-motivo-info">
                            <div className="ux-motivo-icon">
                                <i data-lucide="package-plus"></i>
                            </div>
                            <div className="ux-motivo-details">
                                <h4 className="ux-motivo-name">Unidades a Producir</h4>
                                <p className="ux-motivo-description">Cantidad requerida</p>
                            </div>
                        </div>
                        <div className="ux-input-container">
                            <div className="ux-total-value ux-neutral" id="modal-to-produce-display">--</div>
                            <input type="hidden" id="modal-to-produce" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="ux-modal-footer">
                <div className="ux-footer-left">
                    <button id="view-hourly-sales-btn" className="ux-btn ux-btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i data-lucide="bar-chart-3"></i>
                        Ver Detalle por Horas
                    </button>
                </div>
                <div className="ux-footer-right">
                    <button id="cancel-production-modal-btn" className="ux-btn ux-btn-secondary">
                        Cancelar
                    </button>
                    <button id="apply-production-modal-btn" className="ux-btn ux-btn-primary">
                        Aplicar Cambios
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div id="hourly-sales-modal" className="modal">
        <div className="modal-content">
            <div className="modal-header">
                <h3 id="hourly-sales-modal-title">Ventas por Hora</h3>
                <button className="modal-close" id="hourly-sales-modal-close">&times;</button>
            </div>
            <div className="modal-body">
                <div id="hourly-sales-chart-container">
                </div>
            </div>
        </div>
    </div>
    {forecastModal}
    <script id="processes-data" type="application/json" dangerouslySetInnerHTML={{ __html: processesJson || '[]' }} />

    </>
  );
};

export default DashboardLayout;
